import React, { useState } from 'react';
import { Compass, ArrowRight } from 'lucide-react';
import { sessionsApi } from '../../api/sessions';
import './FoundationOfframpCard.css';

// Session-scoped dismissal so the doors appear once and never nag.
const dismissed = new Set();

/**
 * FoundationOfframpCard — the optional continuation doors that open when a writer
 * confirms their foundation (single-book restate-confirm-route, Piece 3).
 *
 * These are OPTIONS, never a required next step: the AI has already mentioned
 * them once in-conversation (§8); this is the affordance for the same choice.
 * Each door points at a real, addressable target — a now-confirmed Story Bible
 * entry — so "Focus on Clara" resolves to something the model actually sees.
 *
 * Props:
 * - foundation: { confirmed, story_id, options:[{type,template,target_name?,label}] }
 * - onNavigateToSession: (sessionId) => void
 * - onDismiss: () => void
 */
const FoundationOfframpCard = ({ foundation, onNavigateToSession, onDismiss }) => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const storyId = foundation?.story_id;
  const options = foundation?.options || [];
  if (!foundation?.confirmed || !storyId || options.length === 0) return null;
  if (dismissed.has(storyId)) return null;

  const openDoor = async (option) => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const payload =
        option.type === 'focus'
          ? {
              guidance_mode: 'focus',
              template: option.template || 'custom',
              focus_target_name: option.target_name || null,
              name: option.label || 'Focus',
            }
          : {
              guidance_mode: 'explore',
              template: option.template || 'open',
              name: 'Explore',
            };
      const res = await sessionsApi.create(storyId, payload);
      const newId = res?.data?.id || res?.id;
      dismissed.add(storyId);
      if (newId && onNavigateToSession) onNavigateToSession(newId);
    } catch (err) {
      console.error('Failed to open continuation session:', err);
      setError('Could not open that session. Please try again.');
      setCreating(false);
    }
  };

  const handleDismiss = () => {
    dismissed.add(storyId);
    onDismiss?.();
  };

  return (
    <div className="foundation-offramp" role="group" aria-label="Optional next steps">
      <div className="foundation-offramp__lead">
        <span className="foundation-offramp__hint">
          These are here if you want them — or keep going in this conversation.
        </span>
        <button
          type="button"
          className="foundation-offramp__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
      <div className="foundation-offramp__doors">
        {options.map((option, i) => (
          <button
            key={i}
            type="button"
            className="foundation-offramp__door"
            onClick={() => openDoor(option)}
            disabled={creating}
          >
            <Compass className="foundation-offramp__icon" size={15} />
            <span className="foundation-offramp__label">{option.label}</span>
            <ArrowRight className="foundation-offramp__arrow" size={14} />
          </button>
        ))}
      </div>
      {error && <div className="foundation-offramp__error">{error}</div>}
    </div>
  );
};

export default FoundationOfframpCard;
